import React from 'react';

import { Link } from 'react-router-dom';

import './css/InvisibleMark.css'

import { CompareSlider } from './StateOfTheArt';
import { useCursorRimLight } from './rimLight';


// Two things shown rather than said: the marked copy against the original
// under a handle the visitor drags, and a small picture put through what
// images go through online, the mark read back after each one. The
// measurements behind the second stay on the Technology page.


// What the picture goes through, one after the other, each one a class the
// stylesheet turns into a transformation of the picture.
interface Trial {
	label: string
	className: string
}

const TRIALS: Trial[] = [
	{ label: 'resized', className: 'is-resized' },
	{ label: 'cropped', className: 'is-cropped' },
	{ label: 'compressed', className: 'is-compressed' },
	{ label: 'screenshotted', className: 'is-screenshotted' },
	{ label: 'filtered', className: 'is-filtered' },
]

///@brief   How long each trial is shown.
const TRIAL_MS: number = 1700


// The picture, its trial, and the answer: one trial after the other, on a
// loop that runs only while on screen.
function Survives(): React.JSX.Element {

	const [ bReducedMotion ] = React.useState<boolean>( (): boolean =>
		typeof window.matchMedia === 'function' && window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches
	)
	const [ index, setIndex ] = React.useState<number>( 0 )
	const [ bInView, setInView ] = React.useState<boolean>( false )
	const rootRef = React.useRef<HTMLDivElement>( null )

	React.useEffect( (): ( () => void ) => {
		const root: HTMLDivElement | null = rootRef.current
		if ( root === null || typeof IntersectionObserver !== 'function' ) return (): void => {}
		const observer = new IntersectionObserver( ( entries: IntersectionObserverEntry[] ): void => {
			setInView( entries[ 0 ].isIntersecting )
		}, { threshold: 0.5 } )
		observer.observe( root )
		return (): void => observer.disconnect()
	}, [] )

	React.useEffect( (): ( () => void ) => {
		if ( bReducedMotion || !bInView ) return (): void => {}
		const timer: number = window.setTimeout( (): void => {
			setIndex( ( index + 1 ) % TRIALS.length )
		}, TRIAL_MS )
		return (): void => window.clearTimeout( timer )
	}, [ index, bInView, bReducedMotion ] )

	const trial: Trial = TRIALS[ index ]

	return (
		<div
			className={ `Survives ${ trial.className }` }
			ref={ rootRef }
			role="img"
			aria-label="A marked picture is resized, cropped, compressed, screenshotted and filtered in turn; the mark is found after each."
		>
			<div className="Survives-Stage" >
				<svg className="Survives-Photo" viewBox="0 0 88 66" aria-hidden="true" >
					<rect className="Survives-Photo-Paper" x="0.5" y="0.5" width="87" height="65" rx="7" />
					<circle className="Survives-Photo-Sun" cx="20" cy="19" r="6" />
					<path className="Survives-Photo-Hills" d="M6 56 L28 30 L41 45 L53 35 L82 56 Z" />
					<rect className="Survives-Photo-Mark" x="0.5" y="0.5" width="87" height="65" rx="7" fill="url(#Survives-Dots)" />
					<defs>
						<pattern id="Survives-Dots" width="5" height="5" patternUnits="userSpaceOnUse" >
							<circle cx="2.5" cy="2.5" r="0.9" fill="var(--brand-purple-light)" />
						</pattern>
					</defs>
				</svg>
			</div>

			{ /* Keyed on the trial, so each one makes its own entrance. */ }
			<div className="Survives-Readout" >
				<span className="Survives-Trial" key={ `trial-${ index }` } >{ trial.label }</span>
				<span className="Survives-Found" key={ `found-${ index }` } >mark found</span>
			</div>
		</div>
	)
}


export function InvisibleMark(): React.JSX.Element {

	const gridRef = React.useRef<HTMLDivElement>( null )
	useCursorRimLight( gridRef, '.Frame, .Tile' )

	return (
		<div className="Pane-Light InvisibleMark" id="mark" >
			<div className="Delimiter" />
			<div className="Container" >

				<div className="Section-Head" data-fade >
					<div className="Section-Kicker" >Invisible, and built to last</div>
					<div className="Title text-shadow-2" >You can't see it. It doesn't wash off.</div>
				</div>

				<div className="Section-Grid" ref={ gridRef } >

					<div className="Frame InvisibleMark-Frame" data-fade >
						<CompareSlider />
					</div>

					<div className="Tile" data-fade data-fade-delay="0.1" >
						<div className="Tile-Title" >You can't see it</div>
						<div className="Tile-Text" >
							The marked copy looks exactly like the original. Drag the handle to compare.
						</div>
					</div>

					{ /* One big link to the technology page, like the benchmark tile
					     of the speed section and the plans tile of the self-hosted
					     one: the whole tile is the link, and the arrow says so. */ }
					<Link className="Tile Tile-Link InvisibleMark-Survives" to="/technology" data-fade data-fade-delay="0.15" >
						<span className="Tile-Kicker" >Technology</span>
						<span className="Tile-Title" >It doesn't wash off</span>
						<Survives />
						<span className="Tile-Arrow" aria-hidden="true" >→</span>
					</Link>

				</div>

			</div>
		</div>
	)

}
